/**
 * House / flat modes (v2, invisible automations): the only UI is the presence half of the alarm status line
 * ("Disarmed · Home", "Armed · Away since Fri") and one presence row in the alarm sheet. Data: GET /mode
 * (ModeView, server modules/modes); presence.text is computed server-side (tz-aware). Pure.
 */
import type { AnyMode, ModeView, PresenceView } from '@home/shared/modes';

const UNKNOWN = new Set(['', 'unknown', 'unavailable', 'none']);

/**
 * One word for the alarm half: any armed_* (or the server's "Armed away") → "Armed"; disarmed / pending / arming /
 * disarming / triggered keep their own word. Accepts a raw HA state or a server label. null when unknown.
 */
export function alarmWord(s: string | null | undefined): string | null {
  const k = (s ?? '').trim().toLowerCase().replace(/[\s_]+/g, '_');
  if (UNKNOWN.has(k)) return null;
  if (k.startsWith('armed')) return 'Armed';
  if (k === 'alarm') return 'Triggered';
  const w = k.replace(/_/g, ' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/**
 * The home screen / hallway tablet status line. With a presence (members only): "<alarm> · <presence.text>",
 * or just the presence when the alarm state is unknown. Without one (guests, lodgers: GET /mode is 404 / 403)
 * the plain alarm text, unchanged. null when there is nothing to show.
 */
export function statusLine(alarm: string | null | undefined, presence: PresenceView | null | undefined, plain?: string | null): string | null {
  if (!presence) return plain || null;
  const word = alarmWord(alarm);
  return word ? `${word} · ${presence.text}` : presence.text;
}

/** "auto" / "set by you" / "set by Lauren". */
export function presenceSource(p: PresenceView, userId: string | null | undefined): string {
  if (p.source !== 'override') return 'auto';
  if (p.by && userId && p.by === userId) return 'set by you';
  return p.byName ? `set by ${p.byName}` : 'set manually';
}

/** The alarm sheet row: "Presence: Away (auto)", "Presence: Home (set by you)". */
export const presenceRowLabel = (p: PresenceView, userId: string | null | undefined): string =>
  `Presence: ${p.label} (${presenceSource(p, userId)})`;

export type PresenceButton = 'home' | 'away';

/** Which of "I'm home" / "Away" is pressed: the one matching the active override (none while automatic). */
export function presencePressed(v: Pick<ModeView, 'presence' | 'override'>): PresenceButton | null {
  if (v.presence.source !== 'override' || !v.override) return null;
  if (v.override.mode === v.presence.choices.home) return 'home';
  if (v.override.mode === v.presence.choices.away) return 'away';
  return null;
}

/** What pressing a button posts: its mode, or 'auto' (clear) when it is already the active override. */
export function presenceNext(v: Pick<ModeView, 'presence' | 'override'>, b: PresenceButton): AnyMode | 'auto' {
  return presencePressed(v) === b ? 'auto' : v.presence.choices[b];
}
